import { firestoreWrapper } from './firestoreWrapper';

const USER_APP_ACCESS_COLLECTION = 'userAppAccess';
const CACHE_TTL = 30 * 1000; // 30 seconds - much shorter for immediate updates

interface CacheEntry {
  data: string[];
  timestamp: number;
}

// Cache for app access
const appAccessCache = new Map<string, CacheEntry>();

export interface UserAppAccess {
  email: string;
  allowedApps: string[];
}

export const AVAILABLE_APPS = {
  GoodThingTracker: 'good-things',
  PLMS: 'plms',
  PMT2: 'pmt2',
  SOPCentral: 'sop',
  DayAwayBoard: 'dayaway',
  IdeogramPromptStorm: 'ideogram',
  ListingManagementSystem: 'inventory',
  IdeaMachine: 'idea-machine',
  AIDevQuest: 'ai-devquest',
  Glossary: 'glossary',
  EmployeeDirectory: 'employee-directory',
  ProductManager: 'product-manager',
  PPCFarmMailTracker: 'ppc-farm',
  SBSearchTermReport: 'sb-search-term-report',
  FarmingResults: 'farming-results',
  AmazonIndexChecker: 'amazon-index-checker',
  PrimaryLedger: 'primary-ledger',
  ASINSalesHistory: 'sales-and-profit-history',
  DaysOfCover: 'days-of-cover',
  SmartWriter: 'smart-writer',
  // Productivity Apps
  Planova: 'planova',
  QuickShip: 'quickship',
  Whiteboard: 'whiteboard',
  CheckIn: 'checkin',
  // Community Apps
  UnderOneSky: 'under-one-sky',
  PetConnect: 'pet-connect',
  Fadr: 'fadr'
} as const;

// Get app access for a specific user
export const getUserAppAccess = async (email: string): Promise<string[]> => {
  const normalizedEmail = email.toLowerCase();
  const now = Date.now();
  const cached = appAccessCache.get(normalizedEmail);

  // Return cached data if valid
  if (cached && now - cached.timestamp < CACHE_TTL) {
    
    return cached.data;
  }

  
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(USER_APP_ACCESS_COLLECTION),
    normalizedEmail
  );
  const docSnap = await firestoreWrapper.getDoc(docRef);
  
  let allowedApps: string[];
  if (docSnap.exists()) {
    allowedApps = docSnap.data().allowedApps || [];
  } else {
    // By default, grant no access if no permissions are set
    allowedApps = [];
  }

  // Cache the result
  appAccessCache.set(normalizedEmail, { data: allowedApps, timestamp: now });
  return allowedApps;
};

// Get all user app access settings
export const getAllUserAppAccess = async (): Promise<UserAppAccess[]> => {
  const querySnapshot = await firestoreWrapper.getDocs(
    firestoreWrapper.collection(USER_APP_ACCESS_COLLECTION)
  );
  return querySnapshot.docs.map(doc => ({
    email: doc.id,
    allowedApps: doc.data().allowedApps || []
  }));
};

// Update app access for a specific user
export const updateUserAppAccess = async (email: string, allowedApps: string[]): Promise<void> => {
  const normalizedEmail = email.toLowerCase();
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(USER_APP_ACCESS_COLLECTION),
    normalizedEmail
  );
  await firestoreWrapper.setDoc(docRef, { allowedApps }, { merge: true });
  // Clear cache for this user
  appAccessCache.delete(normalizedEmail);
  
  // Clear all authentication caches and broadcast to other tabs
  try {
    const { clearAllAuthCaches, broadcastCacheInvalidation } = await import('./cacheInvalidation');
    await clearAllAuthCaches();
    broadcastCacheInvalidation();
  } catch (error) {
    
  }
};

// Remove app access settings for a user
export const removeUserAppAccess = async (email: string): Promise<void> => {
  const normalizedEmail = email.toLowerCase();
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(USER_APP_ACCESS_COLLECTION),
    normalizedEmail
  );
  await firestoreWrapper.deleteDoc(docRef);
  // Clear cache for this user
  appAccessCache.delete(normalizedEmail);
};

// Check if a user has access to a specific app
export const hasAppAccess = async (email: string, appId: string): Promise<boolean> => {
  // Auto-approve for company emails (business rule)
  if (email.endsWith('@jlsapps.com')) {
    return true;
  }
  
  const allowedApps = await getUserAppAccess(email);
  return allowedApps.includes(appId);
};

// Check if a user has access to the app registry feature
export function hasRegistryAppAccess(email: string, isAdmin: boolean): boolean {
  return isAdmin || !!email;
}

// Clear all app access caches
export const clearAppAccessCache = () => {
  appAccessCache.clear();
};
